import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus, ShoppingCart, Clock, CheckCircle2, Truck, AlertTriangle,
  Pencil, Trash2, MessageSquarePlus, Ban, PackageX, RotateCcw, ExternalLink, X,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatCard } from "@/components/kit/StatCard";
import { SearchInput } from "@/components/kit/SearchInput";
import { FilterShell } from "@/components/kit/FilterShell";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { DataTable } from "@/components/tables/DataTable";
import { RowActions } from "@/components/tables/RowActions";
import { EmptyState } from "@/components/feedback/EmptyState";
import { TableSkeleton } from "@/components/feedback/Skeletons";
import { ConfirmDialog } from "@/components/feedback/ConfirmDialog";
import { OrderDrawer } from "./OrderDrawer";
import { AddNoteDialog } from "./AddNoteDialog";
import { ordersService, storesService, vaAgenciesService } from "@/services/resourceServices";
import { formatApiErrorDetail } from "@/services/api";
import { formatRelativeTime } from "@/lib/format";
import { useAuth } from "@/hooks/useAuth";
import { canWrite, isOwnerRole } from "@/lib/permissions/permissions";
import { ExportButton } from "@/components/kit/ExportButton";
import { MARKETPLACES, ORDER_STATUS_META, orderDisplayStatus } from "@/lib/constants";

const DEFAULT_FILTERS = { search: "", store: "all", marketplace: "all", status: "all", agency: "all", date_from: "", date_to: "" };

export default function OrdersPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const writable = canWrite(user?.role, "orders");
  const owner = isOwnerRole(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const [draft, setDraft] = useState(DEFAULT_FILTERS);
  const [applied, setApplied] = useState(DEFAULT_FILTERS);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [noteFor, setNoteFor] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkShipConfirm, setBulkShipConfirm] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    const status = searchParams.get("status");
    if (status) {
      setDraft((d) => ({ ...d, status }));
      setApplied((a) => ({ ...a, status }));
    }
    if (searchParams.get("new") === "1" && writable) {
      setEditing(null);
      setDrawerOpen(true);
    }
    if (status || searchParams.get("new")) setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const params = useMemo(() => ({
    search: applied.search,
    store_id: applied.store === "all" ? "" : applied.store,
    marketplace: applied.marketplace === "all" ? "" : applied.marketplace,
    status: applied.status === "all" ? "" : applied.status,
    va_agency_id: applied.agency === "all" ? "" : applied.agency,
    date_from: applied.date_from,
    date_to: applied.date_to,
  }), [applied]);

  const listQuery = useQuery({ queryKey: ["orders", params], queryFn: () => ordersService.list(params) });
  const summaryQuery = useQuery({ queryKey: ["orders", "summary"], queryFn: ordersService.summary });
  const storesQuery = useQuery({ queryKey: ["stores", "lite"], queryFn: () => storesService.list() });
  const agenciesQuery = useQuery({ queryKey: ["va-agencies", "lite"], queryFn: () => vaAgenciesService.list() });

  const stores = useMemo(() => storesQuery.data?.items || [], [storesQuery.data]);
  const agencies = useMemo(() => agenciesQuery.data?.items || [], [agenciesQuery.data]);
  const storeMap = useMemo(() => Object.fromEntries(stores.map((s) => [s.id, s.store_name])), [stores]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["orders"] });
    qc.invalidateQueries({ queryKey: ["inventory"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["activity"] });
  };

  // Clear selection whenever filters change so we never ship rows that scrolled out.
  useEffect(() => { setSelected([]); }, [applied]);

  const clearSelection = () => setSelected([]);
  const shippableSelected = useMemo(() => {
    const set = new Set(selected);
    return (listQuery.data?.items || []).filter((r) => set.has(r.id) && r.status !== "shipped");
  }, [selected, listQuery.data]);

  const doBulkShip = async () => {
    if (!shippableSelected.length) { setBulkShipConfirm(false); return; }
    setBulkBusy(true);
    try {
      const res = await ordersService.bulkShip(shippableSelected.map((r) => r.id));
      const { shipped_count = 0, quantity_problems = [], skipped = [], errors = [] } = res;
      if (shipped_count > 0) toast.success(`Shipped ${shipped_count} order${shipped_count === 1 ? "" : "s"} — inventory updated`);
      if (quantity_problems.length) toast.warning(`${quantity_problems.length} moved to Quantity Problem (insufficient stock)`);
      if (skipped.length) toast.info(`${skipped.length} skipped (already shipped)`);
      if (errors.length) toast.error(`${errors.length} failed — check details`);
      if (!shipped_count && !quantity_problems.length && !skipped.length && errors.length) {
        // pure failure — no positive toast
      }
      clearSelection();
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBulkBusy(false);
      setBulkShipConfirm(false);
    }
  };

  const openCreate = () => { setEditing(null); setDrawerOpen(true); };
  const openEdit = (r) => { setEditing(r); setDrawerOpen(true); };

  const cardFilter = (status) => {
    const next = applied.status === status ? "all" : status;
    setDraft((d) => ({ ...d, status: next }));
    setApplied((a) => ({ ...a, status: next }));
  };

  const doShip = async (r) => {
    try {
      const res = await ordersService.ship(r.id);
      if (res.shipped) {
        toast.success(`Order ${r.order_id} shipped — inventory updated`);
      } else {
        const detail = (res.shortfalls || []).map((s) => `${s.sku} (need ${s.needed}, have ${s.available})`).join(", ");
        toast.error(`Insufficient inventory — moved to Quantity Problem: ${detail}`);
      }
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  const runAction = (fn, msg) => async (r) => {
    try { await fn(r.id); toast.success(msg); refresh(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  const doQtyProblem = runAction(ordersService.quantityProblem, "Marked as Quantity Problem");
  const doDidntArrive = runAction(ordersService.didntArrive, "Marked as Didn't Arrive");
  const doReset = runAction(ordersService.reset, "Order reset to Pending");

  const confirmDelete = async () => {
    try { await ordersService.remove(toDelete.id); toast.success("Order deleted"); refresh(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setToDelete(null); }
  };

  const s = summaryQuery.data || {};
  const rows = listQuery.data?.items || [];

  const rowMenu = (r) => {
    const items = [
      { key: "edit", label: "Edit", icon: Pencil, onSelect: () => openEdit(r) },
      { key: "note", label: "Add Note", icon: MessageSquarePlus, onSelect: () => setNoteFor(r) },
    ];
    if (r.status !== "shipped") {
      items.push({ key: "ship", label: "Mark as Shipped", icon: Truck, separatorBefore: true, onSelect: () => doShip(r) });
      items.push({ key: "qty", label: "Quantity Problem", icon: AlertTriangle, onSelect: () => doQtyProblem(r) });
      items.push({ key: "didnt", label: "Didn't Arrive", icon: PackageX, onSelect: () => doDidntArrive(r) });
    }
    if (["quantity_problem", "didnt_arrive"].includes(r.status)) {
      items.push({ key: "reset", label: "Reset to Pending", icon: RotateCcw, onSelect: () => doReset(r) });
    }
    if (owner) {
      items.push({ key: "delete", label: "Delete", icon: Trash2, destructive: true, separatorBefore: true, onSelect: () => setToDelete(r) });
    }
    return items;
  };

  const columns = [
    { key: "order_id", header: "Order ID", render: (r) => <span className="font-medium text-foreground">{r.order_id}</span> },
    { key: "store", header: "Store", render: (r) => storeMap[r.store_id] || <span className="text-muted-foreground">—</span> },
    { key: "marketplace", header: "Marketplace", render: (r) => r.marketplace || "—" },
    { key: "customer", header: "Customer", render: (r) => r.customer_name || <span className="text-muted-foreground">—</span> },
    { key: "sku_count", header: "SKUs", render: (r) => r.sku_count },
    { key: "qty", header: "Qty", render: (r) => r.total_quantity },
    { key: "supplier", header: "Supplier", render: (r) => r.supplier || "—" },
    { key: "supplier_order_id", header: "Supplier Order", render: (r) => r.supplier_order_id || "—" },
    { key: "tracking", header: "Tracking", render: (r) => r.tracking_number || "—" },
    {
      key: "label", header: "Label",
      render: (r) => r.shipping_label_url ? (
        <a href={r.shipping_label_url} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-primary hover:text-brand-dark" data-testid={`order-label-link-${r.id}`}>
          Label <ExternalLink className="h-3.5 w-3.5" />
        </a>
      ) : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "status", header: "Status",
      render: (r) => { const m = ORDER_STATUS_META[orderDisplayStatus(r)]; return <StatusBadge label={m.label} tone={m.tone} />; },
    },
    { key: "updated", header: "Updated", render: (r) => formatRelativeTime(r.updated_at) },
    { key: "actions", header: "", align: "right", render: (r) => (writable ? <RowActions items={rowMenu(r)} /> : null) },
  ];

  const exportColumns = [
    { label: "Order ID", key: "order_id" },
    { label: "Store", get: (r) => storeMap[r.store_id] || "" },
    { label: "Marketplace", key: "marketplace" },
    { label: "Customer", key: "customer_name" },
    { label: "SKUs", key: "sku_count" },
    { label: "Qty", key: "total_quantity" },
    { label: "Tracking", key: "tracking_number" },
    { label: "Status", get: (r) => ORDER_STATUS_META[orderDisplayStatus(r)]?.label || r.status },
    { label: "Updated", get: (r) => r.updated_at },
  ];

  return (
    <div className="space-y-6" data-testid="page-orders">
      <PageHeader
        sticky title="Orders" description="Manage marketplace orders from all connected stores."
        actions={
          <div className="flex items-center gap-2">
            <ExportButton filename="orders" columns={exportColumns} rows={rows} testId="orders-export" />
            {writable && (
              <Button className="gap-2" onClick={openCreate} data-testid="orders-primary-action"><Plus className="h-4 w-4" />New Order</Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testId="stat-pending-orders" label="Pending Orders" value={s.pending ?? "—"} icon={Clock} tone="amber" onClick={() => cardFilter("pending")} active={applied.status === "pending"} />
        <StatCard testId="stat-ready-orders" label="Ready Orders" value={s.ready ?? "—"} icon={CheckCircle2} tone="blue" onClick={() => cardFilter("ready")} active={applied.status === "ready"} />
        <StatCard testId="stat-shipped-orders" label="Shipped Orders" value={s.shipped ?? "—"} icon={Truck} tone="green" onClick={() => cardFilter("shipped")} active={applied.status === "shipped"} />
        <StatCard testId="stat-quantity-problems" label="Quantity Problems" value={s.quantity_problem ?? "—"} icon={AlertTriangle} tone="red" onClick={() => cardFilter("quantity_problem")} active={applied.status === "quantity_problem"} />
      </div>

      <FilterShell
        onApply={() => { setApplied(draft); toast.success("Filters applied"); }}
        onReset={() => { setDraft(DEFAULT_FILTERS); setApplied(DEFAULT_FILTERS); toast("Filters reset"); }}
      >
        <SearchInput value={draft.search} onChange={(v) => setDraft((d) => ({ ...d, search: v }))} placeholder="Order, customer, SKU, tracking…" />
        <Select value={draft.store} onValueChange={(v) => setDraft((d) => ({ ...d, store: v }))}>
          <SelectTrigger className="h-10 bg-background" data-testid="filter-store"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All stores</SelectItem>{stores.map((st) => (<SelectItem key={st.id} value={st.id}>{st.store_name}</SelectItem>))}</SelectContent>
        </Select>
        <Select value={draft.marketplace} onValueChange={(v) => setDraft((d) => ({ ...d, marketplace: v }))}>
          <SelectTrigger className="h-10 bg-background" data-testid="filter-marketplace"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All marketplaces</SelectItem>{MARKETPLACES.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}</SelectContent>
        </Select>
        <Select value={draft.status} onValueChange={(v) => setDraft((d) => ({ ...d, status: v }))}>
          <SelectTrigger className="h-10 bg-background" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="ready">Ready</SelectItem>
            <SelectItem value="shipped">Shipped</SelectItem>
            <SelectItem value="quantity_problem">Quantity Problem</SelectItem>
            <SelectItem value="didnt_arrive">Didn't Arrive</SelectItem>
          </SelectContent>
        </Select>
        <Select value={draft.agency} onValueChange={(v) => setDraft((d) => ({ ...d, agency: v }))}>
          <SelectTrigger className="h-10 bg-background" data-testid="filter-agency"><SelectValue placeholder="Assigned VA" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All VA agencies</SelectItem>{agencies.map((a) => (<SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>))}</SelectContent>
        </Select>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">From</label>
          <input type="date" value={draft.date_from} onChange={(e) => setDraft((d) => ({ ...d, date_from: e.target.value }))}
            className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm shadow-soft focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15" data-testid="filter-date-from" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">To</label>
          <input type="date" value={draft.date_to} onChange={(e) => setDraft((d) => ({ ...d, date_to: e.target.value }))}
            className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm shadow-soft focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15" data-testid="filter-date-to" />
        </div>
      </FilterShell>

      {listQuery.isLoading ? (
        <TableSkeleton rows={6} cols={8} />
      ) : (
        <div className="overflow-x-auto">
          <DataTable
            testId="orders-table"
            paginated
            columns={columns}
            data={rows}
            selectable={writable}
            selectedIds={selected}
            onSelectionChange={setSelected}
            isRowSelectable={(r) => r.status !== "shipped"}
            emptyState={
              <EmptyState icon={ShoppingCart} title="No orders found" testId="orders-empty-state"
                description="Orders from your connected stores will appear here. Create one to get started."
                action={writable ? <Button className="gap-2" onClick={openCreate} data-testid="orders-empty-action"><Plus className="h-4 w-4" /> New Order</Button> : null} />
            }
          />
        </div>
      )}

      {/* Bulk action bar — floats above bottom center whenever rows are selected. */}
      <AnimatePresence>
        {writable && selected.length > 0 && (
          <motion.div
            key="bulk-bar"
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="glass-strong shadow-premium pointer-events-auto fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-2xl border-border/60 px-4 py-3"
            data-testid="orders-bulk-bar"
          >
            <span className="text-sm font-medium text-foreground">
              <span className="font-display text-base font-semibold text-primary">{selected.length}</span> selected
              {shippableSelected.length !== selected.length && (
                <span className="ml-1 text-xs text-muted-foreground">
                  ({shippableSelected.length} shippable)
                </span>
              )}
            </span>
            <span className="h-5 w-px bg-border/60" />
            <Button
              size="sm"
              className="gap-1.5"
              onClick={() => setBulkShipConfirm(true)}
              disabled={!shippableSelected.length || bulkBusy}
              data-testid="orders-bulk-ship-button"
            >
              <Truck className="h-4 w-4" />
              Mark as Shipped
            </Button>
            <button
              type="button"
              onClick={clearSelection}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-ring"
              aria-label="Clear selection"
              data-testid="orders-bulk-clear"
            >
              <X className="h-4 w-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <OrderDrawer open={drawerOpen} onOpenChange={setDrawerOpen} record={editing} stores={stores} onSaved={refresh} />
      <AddNoteDialog open={!!noteFor} onOpenChange={(o) => !o && setNoteFor(null)}
        subtitle={noteFor ? `Order ${noteFor.order_id}` : ""}
        onSubmit={async (msg) => { await ordersService.addNote(noteFor.id, msg); toast.success("Note added"); refresh(); }} />
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}
        title="Delete order?" description={`This permanently removes order "${toDelete?.order_id}" and is recorded in the Activity Log.`}
        confirmLabel="Delete order" destructive onConfirm={confirmDelete} />
      <ConfirmDialog open={bulkShipConfirm} onOpenChange={setBulkShipConfirm}
        title={`Mark ${shippableSelected.length} order${shippableSelected.length === 1 ? "" : "s"} as shipped?`}
        description="Inventory will be deducted for every order. Any order without enough stock is moved to Quantity Problem instead. This action is logged."
        confirmLabel={bulkBusy ? "Shipping…" : "Ship orders"} onConfirm={doBulkShip} />
    </div>
  );
}
